/**
 * gm-pdp-atc-dock — sticky ATC (spec 0062 / ADR-043 + spec 0071).
 *
 * Desktop (≥750px): barra sob o header (2º navbar) quando o ATC real fica
 * parcial ou totalmente coberto pelo navbar — botão sempre 100% acessível.
 * Mobile (<750px): gaveta no bottom com meta + ATC + wishlist (proxy no ATC real).
 *
 * O botão real permanece no slot — a página não muda de altura.
 */
(function () {
  const DESKTOP_MIN = 750;

  class GmPdpAtcDock extends HTMLElement {
    connectedCallback() {
      this.realButton = this.querySelector(
        '.gm-pdp-showcase__add-to-cart, [data-gm-add-to-cart], button[name="add"]'
      );
      const template = this.querySelector('[data-gm-atc-bar-template]');
      if (!this.realButton || !template) return;

      this.visible = false;
      this.scrollRaf = 0;
      this.mode = '';
      this.debug = /(?:^|[?&])atcdebug=1/.test(window.location.search);

      this.bar = template.content.firstElementChild.cloneNode(true);
      this.barInner = this.bar.querySelector('.gm-pdp-sticky-bar__inner');
      this.barVariant = this.bar.querySelector('[data-gm-bar-variant]');
      this.barThumb = this.bar.querySelector('[data-gm-bar-thumb]');
      this.barButton = this.bar.querySelector('.gm-pdp-sticky-bar__atc');
      document.body.appendChild(this.bar);
      window.GmWishlist?.syncAll?.(this.bar);

      this.barButton?.addEventListener('click', () => {
        if (!this.realButton.disabled) this.realButton.click();
      });

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onSwatch = this.onSwatch.bind(this);
      this.onSizeChange = this.onSizeChange.bind(this);

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      document.addEventListener('gm:size-change', this.onSizeChange);
      this.#bindSwatches();
      this.#syncFromActiveSwatch();
      this.#syncAtcDisabled();

      this.#log('connected');
      this.evaluate();
      ensureFormsTeaserLift();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('gm:size-change', this.onSizeChange);
      this.swatches?.forEach((s) => s.removeEventListener('click', this.onSwatch));
      if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
      this.bar?.remove();
      document.body.style.setProperty('--gm-pdp-atc-dock-height', '0px');
      window.dispatchEvent(new CustomEvent('gm-chrome-change'));
    }

    get headerRow() {
      return (
        document.querySelector('#header-component .header__row--top') ||
        document.getElementById('header-component')
      );
    }

    get leftColumn() {
      return document.querySelector('#header-component .header__row--top .header__column--left');
    }

    get rightColumn() {
      return document.querySelector('#header-component .header__row--top .header__column--right');
    }

    #alignToNavbar() {
      const left = this.leftColumn?.getBoundingClientRect();
      const right = this.rightColumn?.getBoundingClientRect();
      if (!this.barInner || !left || !right) return;
      this.barInner.style.width = `${Math.round(right.right - left.left)}px`;
      this.barInner.style.marginLeft = `${Math.round(left.left)}px`;
      this.barInner.style.marginRight = '0';
    }

    #clearNavbarAlign() {
      if (!this.barInner) return;
      this.barInner.style.width = '';
      this.barInner.style.marginLeft = '';
      this.barInner.style.marginRight = '';
    }

    #log(...args) {
      if (this.debug) console.log('[atc-dock]', ...args);
    }

    #bindSwatches() {
      this.swatches = Array.from(
        document.querySelectorAll('.gm-pdp-showcase__swatches [data-gm-intro-color-thumb]')
      );
      this.swatches.forEach((s) => s.addEventListener('click', this.onSwatch));
    }

    onSwatch(event) {
      this.#syncFromSwatch(event.currentTarget);
    }

    #syncFromActiveSwatch() {
      const active =
        this.swatches?.find((s) => s.classList.contains('is-active')) || this.swatches?.[0];
      if (active) this.#syncFromSwatch(active);
    }

    #syncFromSwatch(swatch) {
      if (!swatch) return;
      const label = swatch.getAttribute('data-color-label') || '';
      const img = swatch.querySelector('img');
      if (label && this.barVariant) this.barVariant.textContent = label;
      if (img && this.barThumb) {
        this.barThumb.src = img.currentSrc || img.src;
        if (label) this.barThumb.alt = label;
      }
    }

    onSizeChange(event) {
      const sizeLabel =
        document.querySelector('[data-gm-size-value]')?.textContent?.trim() || '';
      if (sizeLabel && this.barVariant) {
        const color = this.barVariant.textContent || '';
        // Keep color if present; append size
        const base = color.replace(/\s*·\s*[A-Z]{1,3}$/i, '').trim();
        const colorPart = base || color;
        this.barVariant.textContent = colorPart ? `${colorPart} · ${sizeLabel}` : sizeLabel;
      }
      this.#syncAtcDisabled();
      const available = event?.detail?.available !== false;
      if (this.barButton) {
        this.barButton.disabled = !available || !!this.realButton?.disabled;
        this.barButton.setAttribute(
          'aria-disabled',
          this.barButton.disabled ? 'true' : 'false'
        );
      }
    }

    #syncAtcDisabled() {
      if (!this.barButton || !this.realButton) return;
      this.barButton.disabled = !!this.realButton.disabled;
      this.barButton.setAttribute(
        'aria-disabled',
        this.realButton.disabled ? 'true' : 'false'
      );
    }

    onScroll() {
      if (this.scrollRaf) return;
      this.scrollRaf = requestAnimationFrame(() => {
        this.scrollRaf = 0;
        this.evaluate();
      });
    }

    onResize() {
      this.evaluate();
    }

    #setMode(mode) {
      if (!this.bar) return;
      if (this.mode === mode) return;
      this.mode = mode;
      this.bar.classList.toggle('gm-pdp-sticky-bar--mobile', mode === 'mobile');
      this.bar.classList.toggle('gm-pdp-sticky-bar--desktop', mode === 'desktop');
      if (mode === 'mobile') {
        this.bar.style.top = '';
        this.#clearNavbarAlign();
      }
    }

    evaluate() {
      // Observa o slot do ATC no fluxo (não o clone sticky).
      const slot =
        this.querySelector('[data-gm-atc-slot]') || this.realButton?.parentElement || this.realButton;
      const real = slot?.getBoundingClientRect();
      if (!real || !this.bar) return;

      const isDesktop = window.innerWidth >= DESKTOP_MIN;
      this.#setMode(isDesktop ? 'desktop' : 'mobile');

      let shouldShow = false;

      if (isDesktop) {
        const header = this.headerRow?.getBoundingClientRect();
        if (!header) return;
        this.bar.style.top = `${Math.round(header.bottom) - 1}px`;
        this.#alignToNavbar();
        // Mostra assim que o ATC começa a ser coberto pelo navbar (não só
        // quando some por completo) — botão sticky sempre 100% visível.
        shouldShow = real.top < header.bottom;
      } else {
        // Só abre a gaveta depois que o ATC do fluxo rolou para cima (saiu).
        // Não abrir enquanto o botão ainda está abaixo do fold.
        shouldShow = real.bottom <= 0;
      }

      this.#log('evaluate', {
        mode: this.mode,
        shouldShow,
        realBottom: Math.round(real.bottom),
      });

      if (shouldShow) this.show();
      else this.hide();

      if (this.visible) this.#syncChromeHeight();
    }

    #syncChromeHeight() {
      const h =
        this.visible && this.bar
          ? Math.round(this.bar.getBoundingClientRect().height)
          : 0;
      document.body.style.setProperty('--gm-pdp-atc-dock-height', `${h}px`);
      window.dispatchEvent(new CustomEvent('gm-chrome-change'));
      ensureFormsTeaserLift();
    }

    show() {
      if (this.visible || !this.bar) return;
      this.visible = true;
      this.bar.classList.add('is-visible');
      this.bar.removeAttribute('aria-hidden');
      this.#syncChromeHeight();
      this.#log('show');
    }

    hide() {
      if (!this.visible || !this.bar) return;
      this.visible = false;
      this.bar.classList.remove('is-visible');
      this.bar.setAttribute('aria-hidden', 'true');
      this.#syncChromeHeight();
      this.#log('hide');
    }
  }

  if (!customElements.get('gm-pdp-atc-dock')) {
    customElements.define('gm-pdp-atc-dock', GmPdpAtcDock);
  }

  /**
   * Shopify Forms pinta o teaser no shadow root com bottom inline.
   * Injeta uma regra que sobe só o chip (sem form) quando a gaveta
   * mobile publica --gm-forms-teaser-lift.
   */
  function ensureFormsTeaserLift() {
    const styleId = 'gm-forms-teaser-lift';
    const css = `
      #app-embed[data-position="bottom-left"]:not(:has(form)) {
        bottom: var(--gm-forms-teaser-lift, 0px) !important;
        transition: bottom 0.42s cubic-bezier(0.22, 1, 0.36, 1);
      }
      @media (prefers-reduced-motion: reduce) {
        #app-embed[data-position="bottom-left"]:not(:has(form)) {
          transition: none;
        }
      }
    `;

    document.querySelectorAll('shopify-forms-embed').forEach((host) => {
      const root = host.shadowRoot;
      if (!root || root.getElementById(styleId)) return;
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = css;
      root.appendChild(style);
    });
  }

  ensureFormsTeaserLift();
  const formsLiftObserver = new MutationObserver(ensureFormsTeaserLift);
  formsLiftObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
